import React, { createContext, useContext, useState, useCallback } from 'react'
import Toast, { ToastData, ToastType } from '../components/common/Toast'

interface ToastContextType {
  showToast: (type: ToastType, title: string, options?: {
    message?: string
    duration?: number
    action?: {
      label: string
      onClick: () => void
    }
  }) => void
  
  // Funciones de conveniencia
  success: (title: string, message?: string, duration?: number) => void
  error: (title: string, message?: string, duration?: number) => void
  warning: (title: string, message?: string, duration?: number) => void
  info: (title: string, message?: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const showToast = useCallback((
    type: ToastType, 
    title: string, 
    options?: {
      message?: string
      duration?: number
      action?: {
        label: string
        onClick: () => void
      }
    }
  ) => {
    const id = Math.random().toString(36).substring(2, 15)
    const duration = options?.duration !== undefined ? options.duration : 5000

    const newToast: ToastData = {
      id,
      type,
      title,
      message: options?.message,
      duration,
      action: options?.action
    }

    setToasts(prev => [...prev, newToast])
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id))
  }, [])

  // Funciones de conveniencia
  const success = useCallback((title: string, message?: string, duration = 5000) => {
    showToast('success', title, { message, duration })
  }, [showToast])

  const error = useCallback((title: string, message?: string, duration = 7000) => {
    showToast('error', title, { message, duration })
  }, [showToast])

  const warning = useCallback((title: string, message?: string, duration = 6000) => {
    showToast('warning', title, { message, duration })
  }, [showToast])

  const info = useCallback((title: string, message?: string, duration = 5000) => {
    showToast('info', title, { message, duration })
  }, [showToast])

  const value: ToastContextType = {
    showToast,
    success,
    error,
    warning,
    info
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Container de toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              toast={toast}
              onClose={removeToast}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}